import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Briefcase, Users, UserPlus, Calendar, Plus, RefreshCw, Eye, CheckCircle, Clock, XCircle, FileText, Star, Link2, Copy, Download,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRupiah = (num) => {
  if (!num) return "-";
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);
};

const RecruitmentPage = () => {
  const [activeTab, setActiveTab] = useState("jobs");
  const [loading, setLoading] = useState(false);
  const qrCanvasRef = useRef(null);
  const publicApplyUrl = `${window.location.origin}/apply`;
  
  // Data
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  
  // Filters
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  
  // Dialogs
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showApplicantDialog, setShowApplicantDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  
  // Forms
  const [jobForm, setJobForm] = useState({
    judul: "", department_id: "", position_id: "", deskripsi: "", persyaratan: "",
    lokasi: "", tipe_kerja: "full-time", gaji_min: "", gaji_max: "", batas_lamaran: ""
  });
  const [applicantForm, setApplicantForm] = useState({
    job_id: "", nama: "", email: "", telepon: "", alamat: "",
    pendidikan: "", pengalaman_tahun: "", gaji_harapan: "", catatan: ""
  });

  useEffect(() => {
    fetchDepartments();
    fetchPositions();
    fetchJobs();
  }, []);

  useEffect(() => {
    if (activeTab === "applicants") fetchApplicants();
  }, [activeTab, selectedJob, selectedStatus]);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/departments`, { headers: { Authorization: `Bearer ${token}` } });
      setDepartments(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPositions = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/positions`, { headers: { Authorization: `Bearer ${token}` } });
      setPositions(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/jobs`, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(res.data);
    } catch (err) { toast.error("Gagal memuat lowongan"); }
    finally { setLoading(false); }
  };

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      let url = `${API_URL}/api/applicants`;
      const params = [];
      if (selectedJob !== "all") params.push(`job_id=${selectedJob}`);
      if (selectedStatus !== "all") params.push(`status=${selectedStatus}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setApplicants(res.data);
    } catch (err) { toast.error("Gagal memuat pelamar"); }
    finally { setLoading(false); }
  };

  const handleCreateJob = async () => {
    if (!jobForm.judul || !jobForm.department_id || !jobForm.deskripsi) {
      toast.error("Judul, departemen, dan deskripsi wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/jobs`, {
        ...jobForm,
        gaji_min: jobForm.gaji_min ? parseFloat(jobForm.gaji_min) : null,
        gaji_max: jobForm.gaji_max ? parseFloat(jobForm.gaji_max) : null,
        position_id: jobForm.position_id || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Lowongan berhasil dibuat");
      setShowJobDialog(false);
      setJobForm({ judul: "", department_id: "", position_id: "", deskripsi: "", persyaratan: "", lokasi: "", tipe_kerja: "full-time", gaji_min: "", gaji_max: "", batas_lamaran: "" });
      fetchJobs();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membuat lowongan"); }
    finally { setLoading(false); }
  };

  const handleCreateApplicant = async () => {
    if (!applicantForm.job_id || !applicantForm.nama || !applicantForm.email) {
      toast.error("Lowongan, nama, dan email wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/applicants`, {
        ...applicantForm,
        pengalaman_tahun: parseInt(applicantForm.pengalaman_tahun) || 0,
        gaji_harapan: applicantForm.gaji_harapan ? parseFloat(applicantForm.gaji_harapan) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pelamar berhasil ditambahkan");
      setShowApplicantDialog(false);
      setApplicantForm({ job_id: "", nama: "", email: "", telepon: "", alamat: "", pendidikan: "", pengalaman_tahun: "", gaji_harapan: "", catatan: "" });
      fetchApplicants();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menambah pelamar"); }
    finally { setLoading(false); }
  };

  const handleUpdateJobStatus = async (jobId, status) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.put(`${API_URL}/api/jobs/${jobId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status diubah ke ${status}`);
      fetchJobs();
    } catch (err) { toast.error("Gagal update status"); }
  };

  const handleUpdateApplicantStatus = async (applicantId, status) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.put(`${API_URL}/api/applicants/${applicantId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status diubah ke ${status}`);
      fetchApplicants();
    } catch (err) { toast.error("Gagal update status"); }
  };

  const getJobStatusBadge = (status) => {
    switch(status) {
      case "draft": return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Draft</Badge>;
      case "published": return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Published</Badge>;
      case "closed": return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Closed</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getApplicantStatusBadge = (status) => {
    const colors = {
      new: "bg-blue-500", screening: "bg-yellow-500", interview: "bg-purple-500",
      test: "bg-indigo-500", offer: "bg-orange-500", hired: "bg-green-500", rejected: "bg-red-500"
    };
    return <Badge className={colors[status] || "bg-slate-500"}>{status}</Badge>;
  };

  const copyApplyLink = () => {
    navigator.clipboard.writeText(publicApplyUrl);
    toast.success("Link disalin ke clipboard");
  };

  const downloadQr = () => {
    const canvas = qrCanvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "qr-form-pelamar-silsa.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div className="p-6 space-y-6" data-testid="recruitment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Recruitment</h1>
          <p className="text-slate-600">Kelola lowongan dan pelamar</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase className="w-4 h-4 mr-2" />Lowongan</TabsTrigger>
          <TabsTrigger value="applicants" data-testid="tab-applicants"><Users className="w-4 h-4 mr-2" />Pelamar</TabsTrigger>
        </TabsList>

        {/* Jobs Tab */}
        <TabsContent value="jobs" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex justify-between">
                <Button variant="outline" onClick={fetchJobs}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
                <Button onClick={() => setShowJobDialog(true)} data-testid="btn-add-job">
                  <Plus className="w-4 h-4 mr-2" />Tambah Lowongan
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job) => (
              <Card key={job.id} className="border">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{job.judul}</CardTitle>
                    {getJobStatusBadge(job.status)}
                  </div>
                  <CardDescription>{job.department_nama} • {job.lokasi}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tipe:</span>
                      <span>{job.tipe_kerja}</span>
                    </div>
                    {job.gaji_min && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Gaji:</span>
                        <span>{formatRupiah(job.gaji_min)} - {formatRupiah(job.gaji_max)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Batas:</span>
                      <span>{job.batas_lamaran}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Pelamar:</span>
                      <Badge variant="outline">{job.jumlah_pelamar}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {job.status === "draft" && (
                      <Button size="sm" onClick={() => handleUpdateJobStatus(job.id, "published")}>Publish</Button>
                    )}
                    {job.status === "published" && (
                      <Button size="sm" variant="outline" onClick={() => handleUpdateJobStatus(job.id, "closed")}>Close</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Applicants Tab */}
        <TabsContent value="applicants" className="space-y-4">
          <Card data-testid="public-apply-link-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4" />Link Pendaftaran Publik</CardTitle>
              <CardDescription>Bagikan link atau QR ini ke pelamar. Data yang mereka isi otomatis masuk ke daftar pelamar di bawah.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-4">
              <div ref={qrCanvasRef} className="p-2 bg-white border rounded-lg">
                <QRCodeCanvas value={publicApplyUrl} size={110} />
              </div>
              <div className="flex-1 min-w-[220px] space-y-2">
                <Input readOnly value={publicApplyUrl} data-testid="public-apply-link-input" />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={copyApplyLink} data-testid="copy-apply-link-btn">
                    <Copy className="w-4 h-4 mr-1" />Copy Link
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadQr} data-testid="download-qr-btn">
                    <Download className="w-4 h-4 mr-1" />Download QR
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4">
                  <div className="w-48">
                    <Label>Lowongan</Label>
                    <Select value={selectedJob} onValueChange={setSelectedJob}>
                      <SelectTrigger><SelectValue placeholder="Semua" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Lowongan</SelectItem>
                        {jobs.map((j) => (<SelectItem key={j.id} value={j.id}>{j.judul}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-40">
                    <Label>Status</Label>
                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                      <SelectTrigger><SelectValue placeholder="Semua" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Status</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="screening">Screening</SelectItem>
                        <SelectItem value="interview">Interview</SelectItem>
                        <SelectItem value="test">Test</SelectItem>
                        <SelectItem value="offer">Offer</SelectItem>
                        <SelectItem value="hired">Hired</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={() => setShowApplicantDialog(true)} data-testid="btn-add-applicant">
                  <UserPlus className="w-4 h-4 mr-2" />Tambah Pelamar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Daftar Pelamar</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : applicants.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada pelamar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nama</TableHead>
                      <TableHead>Lowongan</TableHead>
                      <TableHead>Pendidikan</TableHead>
                      <TableHead>Pengalaman</TableHead>
                      <TableHead>Gaji Harapan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applicants.map((a) => (
                      <TableRow key={a.id} data-testid={`applicant-row-${a.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {a.foto_url ? (
                              <img src={a.foto_url} alt={a.nama} className="w-9 h-9 rounded-full object-cover shrink-0" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                <Users className="w-4 h-4 text-slate-400" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{a.nama}</p>
                              <p className="text-sm text-slate-500">{a.telepon}{a.email ? ` • ${a.email}` : ""}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{a.job_judul || <span className="text-slate-400">Umum</span>}</TableCell>
                        <TableCell>{a.pendidikan || "-"}</TableCell>
                        <TableCell>{a.pengalaman_tahun != null ? `${a.pengalaman_tahun} tahun` : "-"}</TableCell>
                        <TableCell>{formatRupiah(a.gaji_harapan)}</TableCell>
                        <TableCell>{getApplicantStatusBadge(a.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedApplicant(a); setShowDetailDialog(true); }}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {a.status !== "hired" && a.status !== "rejected" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleUpdateApplicantStatus(a.id, "hired")}>
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleUpdateApplicantStatus(a.id, "rejected")}>
                                  <XCircle className="w-4 h-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Job Dialog */}
      <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Tambah Lowongan</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Judul Posisi *</Label><Input value={jobForm.judul} onChange={(e) => setJobForm({...jobForm, judul: e.target.value})} placeholder="Software Engineer" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Departemen *</Label>
                <Select value={jobForm.department_id} onValueChange={(v) => setJobForm({...jobForm, department_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => (<SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Posisi</Label>
                <Select value={jobForm.position_id} onValueChange={(v) => setJobForm({...jobForm, position_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{positions.map((p) => (<SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deskripsi *</Label><Textarea value={jobForm.deskripsi} onChange={(e) => setJobForm({...jobForm, deskripsi: e.target.value})} placeholder="Deskripsi pekerjaan..." rows={3} /></div>
            <div><Label>Persyaratan</Label><Textarea value={jobForm.persyaratan} onChange={(e) => setJobForm({...jobForm, persyaratan: e.target.value})} placeholder="- Min S1\n- 2 tahun pengalaman" rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Lokasi</Label><Input value={jobForm.lokasi} onChange={(e) => setJobForm({...jobForm, lokasi: e.target.value})} placeholder="Jakarta" /></div>
              <div><Label>Tipe Kerja</Label>
                <Select value={jobForm.tipe_kerja} onValueChange={(v) => setJobForm({...jobForm, tipe_kerja: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Gaji Min</Label><Input type="number" value={jobForm.gaji_min} onChange={(e) => setJobForm({...jobForm, gaji_min: e.target.value})} placeholder="5000000" /></div>
              <div><Label>Gaji Max</Label><Input type="number" value={jobForm.gaji_max} onChange={(e) => setJobForm({...jobForm, gaji_max: e.target.value})} placeholder="10000000" /></div>
              <div><Label>Batas Lamaran</Label><Input type="date" value={jobForm.batas_lamaran} onChange={(e) => setJobForm({...jobForm, batas_lamaran: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJobDialog(false)}>Batal</Button>
            <Button onClick={handleCreateJob} disabled={loading}>{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Applicant Dialog */}
      <Dialog open={showApplicantDialog} onOpenChange={setShowApplicantDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Tambah Pelamar</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Lowongan *</Label>
              <Select value={applicantForm.job_id} onValueChange={(v) => setApplicantForm({...applicantForm, job_id: v})}>
                <SelectTrigger><SelectValue placeholder="Pilih lowongan" /></SelectTrigger>
                <SelectContent>{jobs.filter(j => j.status === 'published').map((j) => (<SelectItem key={j.id} value={j.id}>{j.judul}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Nama Lengkap *</Label><Input value={applicantForm.nama} onChange={(e) => setApplicantForm({...applicantForm, nama: e.target.value})} placeholder="John Doe" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Email *</Label><Input type="email" value={applicantForm.email} onChange={(e) => setApplicantForm({...applicantForm, email: e.target.value})} placeholder="john@email.com" /></div>
              <div><Label>Telepon</Label><Input value={applicantForm.telepon} onChange={(e) => setApplicantForm({...applicantForm, telepon: e.target.value})} placeholder="08123456789" /></div>
            </div>
            <div><Label>Alamat</Label><Input value={applicantForm.alamat} onChange={(e) => setApplicantForm({...applicantForm, alamat: e.target.value})} placeholder="Alamat lengkap" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Pendidikan</Label><Input value={applicantForm.pendidikan} onChange={(e) => setApplicantForm({...applicantForm, pendidikan: e.target.value})} placeholder="S1 Teknik Informatika" /></div>
              <div><Label>Pengalaman (tahun)</Label><Input type="number" value={applicantForm.pengalaman_tahun} onChange={(e) => setApplicantForm({...applicantForm, pengalaman_tahun: e.target.value})} placeholder="3" /></div>
            </div>
            <div><Label>Gaji Harapan</Label><Input type="number" value={applicantForm.gaji_harapan} onChange={(e) => setApplicantForm({...applicantForm, gaji_harapan: e.target.value})} placeholder="8000000" /></div>
            <div><Label>Catatan</Label><Textarea value={applicantForm.catatan} onChange={(e) => setApplicantForm({...applicantForm, catatan: e.target.value})} placeholder="Catatan tambahan" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicantDialog(false)}>Batal</Button>
            <Button onClick={handleCreateApplicant} disabled={loading}>{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applicant Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detail Pelamar</DialogTitle>
            <DialogDescription>Informasi lengkap biodata dan status lamaran pelamar</DialogDescription>
          </DialogHeader>
          {selectedApplicant && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto" data-testid="applicant-detail-content">
              {selectedApplicant.foto_url && (
                <img src={selectedApplicant.foto_url} alt={selectedApplicant.nama} className="w-24 h-24 rounded-lg object-cover mx-auto" data-testid="applicant-detail-photo" />
              )}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Data Umum</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-500">Nama</Label><p className="font-medium">{selectedApplicant.nama}</p></div>
                  <div><Label className="text-slate-500">Status</Label><p>{getApplicantStatusBadge(selectedApplicant.status)}</p></div>
                  <div><Label className="text-slate-500">Telepon</Label><p>{selectedApplicant.telepon || "-"}</p></div>
                  <div><Label className="text-slate-500">Email</Label><p>{selectedApplicant.email || "-"}</p></div>
                  <div><Label className="text-slate-500">Pendidikan</Label><p>{selectedApplicant.pendidikan || "-"}</p></div>
                  <div><Label className="text-slate-500">Lowongan</Label><p>{selectedApplicant.job_judul || "Umum (Tanpa Lowongan)"}</p></div>
                  <div><Label className="text-slate-500">Alamat</Label><p>{selectedApplicant.alamat || "-"}</p></div>
                </div>
              </div>

              {(selectedApplicant.tempat_lahir || selectedApplicant.no_ktp || selectedApplicant.jenis_kelamin) && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Biodata Pribadi</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-500">Nama Panggilan</Label><p>{selectedApplicant.nama_panggilan || "-"}</p></div>
                    <div><Label className="text-slate-500">Tempat/Tgl Lahir</Label><p>{selectedApplicant.tempat_lahir || "-"} / {selectedApplicant.tanggal_lahir || "-"}</p></div>
                    <div><Label className="text-slate-500">Agama</Label><p>{selectedApplicant.agama || "-"}</p></div>
                    <div><Label className="text-slate-500">Jenis Kelamin</Label><p>{selectedApplicant.jenis_kelamin || "-"}</p></div>
                    <div><Label className="text-slate-500">Status Pernikahan</Label><p>{selectedApplicant.status_pernikahan || "-"}</p></div>
                    <div><Label className="text-slate-500">No. KTP</Label><p>{selectedApplicant.no_ktp || "-"}</p></div>
                    <div><Label className="text-slate-500">No. NPWP</Label><p>{selectedApplicant.no_npwp || "-"}</p></div>
                    <div><Label className="text-slate-500">No. KTA</Label><p>{selectedApplicant.no_kta || "-"}</p></div>
                    <div><Label className="text-slate-500">Tinggi/Berat</Label><p>{selectedApplicant.tinggi_badan || "-"} cm / {selectedApplicant.berat_badan || "-"} kg</p></div>
                    <div><Label className="text-slate-500">Baju/Celana/Sepatu</Label><p>{selectedApplicant.ukuran_baju || "-"} / {selectedApplicant.ukuran_celana || "-"} / {selectedApplicant.ukuran_sepatu || "-"}</p></div>
                  </div>
                </div>
              )}

              {selectedApplicant.nama_pasangan && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Pasangan</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-500">Nama</Label><p>{selectedApplicant.nama_pasangan}</p></div>
                    <div><Label className="text-slate-500">Pekerjaan</Label><p>{selectedApplicant.pekerjaan_pasangan || "-"}</p></div>
                  </div>
                </div>
              )}

              {(selectedApplicant.nama_ayah || selectedApplicant.nama_ibu) && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Orang Tua</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-500">Nama Ayah</Label><p>{selectedApplicant.nama_ayah || "-"}</p></div>
                    <div><Label className="text-slate-500">Nama Ibu</Label><p>{selectedApplicant.nama_ibu || "-"}</p></div>
                  </div>
                </div>
              )}

              {selectedApplicant.anak?.filter((c) => c.nama?.trim()).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Anak ({selectedApplicant.anak.filter((c) => c.nama?.trim()).length})</p>
                  {selectedApplicant.anak.filter((c) => c.nama?.trim()).map((c, i) => (
                    <p key={i} className="text-sm">{c.nama} ({c.jenis_kelamin || "-"}, {c.usia || "-"} th) - {c.pendidikan_terakhir || "-"}</p>
                  ))}
                </div>
              )}

              {selectedApplicant.pengalaman_kerja_list?.filter((k) => k.nama_perusahaan?.trim()).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Pengalaman Kerja</p>
                  {selectedApplicant.pengalaman_kerja_list.filter((k) => k.nama_perusahaan?.trim()).map((k, i) => (
                    <p key={i} className="text-sm">{k.nama_perusahaan} - {k.posisi || "-"} ({k.tanggal_mulai || "-"} s/d {k.tanggal_selesai || "sekarang"})</p>
                  ))}
                </div>
              )}

              {selectedApplicant.keahlian && (
                <div><Label className="text-slate-500">Keahlian</Label><p className="text-sm">{selectedApplicant.keahlian}</p></div>
              )}
              {selectedApplicant.catatan && (
                <div><Label className="text-slate-500">Catatan</Label><p>{selectedApplicant.catatan}</p></div>
              )}
              <div className="flex gap-2 pt-4">
                <Button size="sm" variant="outline" onClick={() => handleUpdateApplicantStatus(selectedApplicant.id, "screening")}>Screening</Button>
                <Button size="sm" variant="outline" onClick={() => handleUpdateApplicantStatus(selectedApplicant.id, "interview")}>Interview</Button>
                <Button size="sm" variant="outline" onClick={() => handleUpdateApplicantStatus(selectedApplicant.id, "test")}>Test</Button>
                <Button size="sm" variant="outline" onClick={() => handleUpdateApplicantStatus(selectedApplicant.id, "offer")}>Offer</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecruitmentPage;
